<template>
	<v-card class="edit-race-card rounded-lg">
		<div class="edit-race-header">
			<h1>Editar carrera</h1>
		</div>
		<v-form ref="editRaceForm" v-model="formValid" class="edit-race-form">
			<!-- Name of the race -->
			<v-text-field
				label="Nombre de la carrera"
				v-model="name"
				:rules="[rules.notEmpty]"
			></v-text-field>

			<v-textarea
				label="Descripción de la ruta"
				v-model="description"
			></v-textarea>

			<div class="card-row">
				<!-- Range date selector -->
				<v-dialog
					ref="finishDateDialog"
					v-model="finishDateModal"
					:return-value.sync="finishDate"
					persistent
					width="290px"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							:value="finishDate | formatDate"
							label="Fecha de fin"
							prepend-icon="mdi-calendar"
							readonly
							v-bind="attrs"
							v-on="on"
							:rules="[rules.notEmpty, correctDates]"
						></v-text-field>
					</template>
					<v-date-picker
						v-model="finishDate"
						scrollable
						locale="es-ES"
						first-day-of-week="1"
						reactive
						:allowed-dates="allowedDates"
						@change="$refs.finishDateDialog.save(finishDate)"
					>
						<v-spacer></v-spacer>
						<v-btn
							text
							class="rounded-lg"
							color="primary"
							@click="finishDateModal = false"
							>Cancel</v-btn
						>
					</v-date-picker>
				</v-dialog>

				<v-dialog
					ref="finishTimeDialog"
					v-model="finishTimeModal"
					:return-value.sync="finishTime"
					persistent
					width="290px"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							:value="finishTime"
							label="Hora de fin"
							prepend-icon="mdi-calendar"
							readonly
							v-bind="attrs"
							v-on="on"
							:rules="[rules.notEmpty, correctDates]"
						></v-text-field>
					</template>
					<v-time-picker
						v-model="finishTime"
						scrollable
						reactive
						format="24hr"
						@change="$refs.finishTimeDialog.save(finishTime)"
					>
						<v-spacer></v-spacer>
						<v-btn
							class="rounded-lg"
							text
							color="primary"
							@click="finishTimeModal = false"
							>Cancel</v-btn
						>
					</v-time-picker>
				</v-dialog>
			</div>

			<v-text-field
				prepend-icon="mdi-currency-eur"
				label="Precio de la carrera"
				v-model="price"
				lang="es"
				type="number"
				:rules="[rules.notEmpty, rules.positive]"
			></v-text-field>

			<v-combobox
				prepend-icon="mdi-tag-multiple"
				v-model="tags"
				label="Etiquetas carrera"
				multiple
				clearable
				chips
				:items="existingTags"
				:loading="tagsLoading"
				:search-input.sync="searchInput"
				@change="searchInput = ''"
			></v-combobox>

			<v-autocomplete
				prepend-icon="mdi-selection-marker"
				v-model="circuit"
				label="Añadir carrera a circuito"
				clearable
				:items="circuits"
				:loading="circuitsLoading"
				:search-input.sync="circuitSearchInput"
				@change="circuitSearchInput = ''"
			>
				<template #selection="{ item }">{{ item.name }}</template>

				<template #item="{ item }">{{ item.name }}</template>
			</v-autocomplete>

			<!-- ACTION BUTTONS -->
			<v-card-actions class="card-actions">
				<v-spacer></v-spacer>
				<!-- Button to cancel adding the race -->
				<v-btn
					text
					@click="cancel"
					color="error"
					elevation="0"
					class="rounded-lg"
					:disabled="loading"
					>Cancelar</v-btn
				>

				<!-- Button to save the race -->

				<v-btn
					@click="submit"
					color="info"
					:loading="loading"
					:disabled="loading || !formValid"
					elevation="0"
					class="rounded-lg"
					>Editar ruta</v-btn
				>
			</v-card-actions>
		</v-form>
	</v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
	name: "AdminEditRoute",
	props: {
		race: Object,
	},
	data() {
		return {
			//Component data
			loading: false,

			//Form data
			name: this.race.name,
			description: this.race.description,

			finishDateModal: false,
			finishDate: moment(this.race.finishDate).format("YYYY-MM-DD"),
			finishTimeModal: false,
			finishTime: moment(this.race.finishDate).format("HH:mm"),

			tags: this.race.tags,
			existingTags: [],
			tagsLoading: false,
			searchInput: "",

			circuit: this.race.circuit,
			circuits: [],
			circuitsLoading: false,
			circuitSearchInput: undefined,

			modalDateRange: false,
			price: this.race.price / 100,
			formValid: false,

			//Form validation rules
			rules: {
				notEmpty: (v) => v !== "" || "El campo no puede estar vacío",
				positive: (v) => v >= 0 || "El precio no puede ser negativo",
			},
			correctDates: () => {
				if (this && this.finishDate && this.finishTime) {
					return (
						moment(this.race.startDate).valueOf() <
							moment(
								`${this.finishDate} ${this.finishTime}`,
								"YYYY-MM-DD HH:mm"
							).valueOf() ||
						"La fecha y hora de final debe ser posterior a la de inicio"
					);
				} else {
					return true;
				}
			},
		};
	},
	filters: {
		formatDate(date) {
			if (date === "") {
				return "";
			} else {
				return moment(date).locale("es-ES").format("ll");
			}
		},
	},
	watch: {
		finishDate() {
			this.$refs.editRaceForm.validate();
		},
		finishTime() {
			this.$refs.editRaceForm.validate();
		},
	},

	methods: {
		/**
		 * Set allowed dates
		 */
		allowedDates(date) {
			//If the date is greater or equal to today, it is allowed
			return moment(date).isAfter(moment(this.race.finishDate));
		},

		/**
		 * When a file is uploaded, convert it to JS object of coordinates
		 */

		/**
		 * When the user submits the form
		 */
		async submit() {
			try {
				this.$emit("loading", true);
				this.loading = true;
				this.$refs.editRaceForm.validate();

				let payload = {
					name: this.name,
					description: this.description,
					finishDate: moment(
						`${this.finishDate} ${this.finishTime}`,
						"YYYY-MM-DD HH:mm"
					).format(),
					price: this.price * 100,
					tags: this.tags,
					raceId: this.race.id,
					circuit: this.circuit ? this.circuit.id : null,
				};

				//Then show the snackbar
				this.$emit("snackbar-event", {
					color: "info",
					text:
						"Espere mientras se procesa y se actualiza la carrera...",
				});

				//Send the form and the object of coordinates to the back-end
				await axios.patch("admin/edit-route", payload);

				//Then show the snackbar
				this.$emit("snackbar-event", {
					color: "success",
					text: "Carrera actualizada con éxito",
				});
				this.$emit("update");
				this.$emit("close");
			} catch (error) {
				console.log(error);
				if (error.response && error.response.data) {
					if (error.response.status === 401) {
						this.$router.push({ name: "AdminLogin" });
					}
					this.$emit("snackbar-event", {
						color: "error",
						text: error.response.data,
					});
				} else {
					//Show error snackbar
					this.$emit("snackbar-event", {
						color: "error",
						text:
							"Ha ocurrido un error, vuelva a intentarlo más tarde",
					});
				}
			} finally {
				//Hide loading
				this.$emit("loading", false);

				this.loading = false;
			}
		},

		/**
		 * Returns to main page if admin cancels
		 */
		cancel() {
			this.$emit("close");
		},

		/**
		 * Fetches existing tags
		 */
		async fetchTags() {
			try {
				this.tagsLoading = true;
				const response = await axios.get("/races/tags");
				this.existingTags = response.data;

				if (this.circuit) {
					this.circuit = (
						await axios.get(`/circuit/${this.circuit}`)
					).data;
				}
			} catch (error) {
				//No tags is not a problem
				this.existingTags = [];
			} finally {
				this.tagsLoading = false;
			}
		},

		/**
		 * Fetches circuits
		 */
		async fetchCircuits() {
			try {
				this.circuitsLoading = true;
				const response = await axios.get("/circuits");
				this.circuits = response.data.circuits;
			} catch (error) {
				//No circuits is not a problem
				this.circuits = [];
			} finally {
				this.circuitsLoading = false;
			}
		},
	},
	async mounted() {
		this.fetchTags();
		this.fetchCircuits();
		try {
			await axios.post("/is-admin");
		} catch (error) {
			this.$router.push({ name: "AdminLogin" });
		}
	},
};
</script>

<style lang="scss" scoped>
.map {
	height: 75vh;
	width: 100%;
	z-index: 0;
}

@import "../../scss/_mixins.scss";
@import "../../scss/_variables.scss";

.edit-race-card {
	width: 100%;
	max-width: 1400px;
	height: fit-content;

	.edit-race-form {
		padding: 2em;
	}

	.edit-race-header {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		margin: 0;
		padding: 2em;
		background: #292931;

		h1 {
			flex: 1;
			padding: 0 0.5em;
			color: #fff;
			font-size: 2.5em;
		}
	}

	.price-field {
		width: fit-content;
	}

	.card-row {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		column-gap: 1em;
	}

	.card-actions {
		margin-top: 2em;
		padding: 0;
	}
}
</style>
